import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { calculatePensionContribution, formatCurrency, isPartTime as isPartTimeEmp, partTimeFraction as partTimeFractionOf } from '../utils/pensionCalc';
import { MiniPill } from '../components/ui';
import { DIRECTOR_NI_LABELS, personalDetailsSummary } from '../utils/constants';
import EmployeeActionsPopover from '../components/EmployeeActionsPopover';
import PensionModal from '../components/PensionModal';
import LeaveModal from '../components/LeaveModal';
import SickModal from '../components/SickModal';
import UnpaidLeaveModal from '../components/UnpaidLeaveModal';
import AdditionModal from '../components/AdditionModal';
import DeductionModal from '../components/DeductionModal';
import BankDetailsModal from '../components/BankDetailsModal';
import AddressModal from '../components/AddressModal';
import PersonalDetailsModal from '../components/PersonalDetailsModal';
import ContractualChangeModal from '../components/ContractualChangeModal';
import Toast from '../components/Toast';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function tenureOf(startDate) {
  if (!startDate) return null;
  const start = new Date(startDate);
  const now = new Date();
  if (isNaN(start.getTime())) return null;
  if (start > now) return `Starts ${formatDate(startDate)}`;
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  const y = years > 0 ? `${years} year${years === 1 ? '' : 's'}` : '';
  const m = months > 0 ? `${months} month${months === 1 ? '' : 's'}` : '';
  if (!y && !m) return 'less than a month tenure';
  return `${[y, m].filter(Boolean).join(' ')} tenure`;
}

function titleCase(str) {
  if (!str) return str;
  return String(str).replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <dt className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">{label}</dt>
      <dd className={`mt-1 text-[14px] ${highlight ? 'text-brand-700 font-semibold' : 'text-gray-800'}`}>
        {value === null || value === undefined || value === '' ? <span className="text-gray-300">—</span> : value}
      </dd>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
    pending_start: 'bg-amber-100 text-amber-800 ring-amber-200',
    left: 'bg-gray-100 text-gray-700 ring-gray-200',
  };
  const labels = { active: 'Active', pending_start: 'Pending start', left: 'Left' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-[12px] font-semibold ring-1 ${map[status] || map.left}`}>
      {labels[status] || status}
    </span>
  );
}

function RingMeter({ percent, color = '#ab63f2', size = 28 }) {
  const r = (size / 2) - 3;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent || 0));
  const offset = c - (p / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="3" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3"
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

const CHANGE_TYPE_LABELS = {
  salary: 'Salary change', pension: 'Pension change', parental_leave: 'Parental leave',
  sick_leave: 'Sick leave', addition: 'Addition', deduction: 'Deduction',
  bank_details: 'Bank details', address_change: 'Address change', new_starter: 'New starter',
  leaver: 'Leaver', contractual_change: 'Contractual change', personal_details: 'Personal details',
};

function changeSummary(change) {
  const t = change.change_type;
  const d = typeof change.change_data === 'string' ? JSON.parse(change.change_data) : (change.change_data || {});
  if (t === 'salary') return d.new_annual_salary ? `New: ${formatCurrency(d.new_annual_salary)}/yr` : d.new_hourly_rate ? `New: ${formatCurrency(d.new_hourly_rate)}/hr` : 'Updated';
  if (t === 'contractual_change') return d.change_type_label || d.change_type || 'Updated';
  if (t === 'addition') return `${d.addition_type_label || d.addition_type || ''} · ${d.amount ? formatCurrency(d.amount) : ''}`;
  if (t === 'deduction') return `${d.deduction_type_label || d.deduction_type || ''} · ${d.amount ? formatCurrency(d.amount) : ''}`;
  if (t === 'parental_leave') return d.leave_type || 'Parental leave';
  if (t === 'leaver') return `Last day ${formatDate(d.leaving_date || d.leave_date)}`;
  if (t === 'bank_details') return 'Updated bank details';
  if (t === 'address_change') return [d.address_line_1 || d.address, d.postcode].filter(Boolean).join(', ');
  if (t === 'sick_leave') return `From ${formatDate(d.start_date)}`;
  if (t === 'personal_details') return personalDetailsSummary(d);
  return CHANGE_TYPE_LABELS[t] || t;
}

const TABS = [
  { id: 'pay',        label: 'Pay & Pension' },
  { id: 'employment', label: 'Employment' },
  { id: 'personal',   label: 'Personal' },
  { id: 'bank',       label: 'Bank' },
  { id: 'changes',    label: 'Pending changes' },
  { id: 'history',    label: 'History' },
];

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, viewingAs, viewAsClient } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [changes, setChanges] = useState([]);
  const [standingItems, setStandingItems] = useState([]);
  const [submittedItems, setSubmittedItems] = useState([]);
  const [sickRecords, setSickRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pay');

  // Action popover + modal state — mirrors the dashboard wiring so the same
  // 9-tile popover works on this page too.
  const [actionsPopover, setActionsPopover] = useState(null);
  const [pensionModal, setPensionModal] = useState(null);
  const [leaveModal, setLeaveModal] = useState(null);
  const [sickModal, setSickModal] = useState(null);
  const [unpaidLeaveModal, setUnpaidLeaveModal] = useState(null);
  const [additionModal, setAdditionModal] = useState(null);
  const [deductionModal, setDeductionModal] = useState(null);
  const [bankDetailsModal, setBankDetailsModal] = useState(null);
  const [addressModal, setAddressModal] = useState(null);
  const [personalDetailsModal, setPersonalDetailsModal] = useState(null);
  const [contractualChangeModal, setContractualChangeModal] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'error' });

  const isAdminViewing = !!viewingAs;

  const loadEmployee = useCallback(async () => {
    try {
      const [empRes, changesRes, standingRes, sickRes, periodRes] = await Promise.all([
        api.get(`/employees/${id}`),
        api.get('/pending-changes'),
        api.get('/pending-changes/standing').catch(() => ({ data: [] })),
        api.get('/employees/sick-records').catch(() => ({ data: [] })),
        api.get('/submissions/current-period').catch(() => ({ data: null })),
      ]);
      setEmployee(empRes.data);
      setChanges(changesRes.data.filter(c => c.employee_id === id));
      setStandingItems(standingRes.data.filter(c => c.employee_id === id));
      setSickRecords(sickRes.data);

      // One-off additions/deductions submitted for the current period flip to
      // status='submitted' and drop out of /pending-changes. Pull them back in so
      // the "this period" figures match the admin run sheet until the period rolls.
      const periodId = periodRes.data?.id;
      if (periodId) {
        try {
          const submittedRes = await api.get('/pending-changes/submitted', { params: { period_id: periodId } });
          setSubmittedItems(submittedRes.data.filter(c => c.employee_id === id));
        } catch {
          setSubmittedItems([]);
        }
      } else {
        setSubmittedItems([]);
      }
    } catch (err) {
      console.error('Failed to load employee:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  const submitChange = async (changeType, employeeId, changeData) => {
    const payload = {
      change_type: changeType,
      employee_id: employeeId,
      change_data: {
        employee_name: `${employee.first_name} ${employee.last_name}`,
        ...changeData,
      },
    };
    if (isAdminViewing) {
      await api.post('/pending-changes/admin-direct', payload);
      setToast({
        message: 'Applied directly (admin override). This does not create a pending change.',
        type: 'info',
      });
    } else {
      await api.post('/pending-changes', payload);
    }
  };

  const handlePensionSave = async (employeeId, pensionData) => {
    if (isAdminViewing) {
      await api.put(`/pensions/${employeeId}/admin`, pensionData);
    } else {
      await api.put(`/pensions/${employeeId}`, pensionData);
    }
    await loadEmployee();
  };
  const handleLeaveSave = async (employeeId, data) => { await submitChange('parental_leave', employeeId, data); await loadEmployee(); };
  const handleSickSave = async (employeeId, data) => { await submitChange('sick_leave', employeeId, data); await loadEmployee(); };
  const handleUnpaidLeaveSave = async (employeeId, data) => { await submitChange('unpaid_leave', employeeId, data); await loadEmployee(); };
  const handleAdditionSave = async (employeeId, data) => { await submitChange('addition', employeeId, data); await loadEmployee(); };
  const handleDeductionSave = async (employeeId, data) => { await submitChange('deduction', employeeId, data); await loadEmployee(); };
  const handleBankDetailsSave = async (employeeId, data) => {
    await submitChange('bank_details', employeeId, {
      bank_account_name: data.bank_account_name,
      sort_code: data.sort_code,
      account_number: data.account_number,
    });
    await loadEmployee();
  };
  const handleAddressSave = async (employeeId, data) => {
    await submitChange('address_change', employeeId, {
      address: data.address,
      address_line_2: data.address_line_2,
      city: data.city,
      postcode: data.postcode,
    });
    await loadEmployee();
  };
  const handlePersonalDetailsSave = async (employeeId, data) => { await submitChange('personal_details', employeeId, data); await loadEmployee(); };
  const handleContractualChangeSave = async (employeeId, data) => { await submitChange('contractual_change', employeeId, data); await loadEmployee(); };

  const openActionsPopover = (e) => {
    if (!employee) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setActionsPopover({
      emp: employee,
      rect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
    });
  };

  const handleActionSelect = (actionId, emp) => {
    setActionsPopover(null);
    if (actionId === 'addition') setAdditionModal(emp);
    else if (actionId === 'deduction') setDeductionModal(emp);
    else if (actionId === 'bank_details') setBankDetailsModal(emp);
    else if (actionId === 'address') setAddressModal(emp);
    else if (actionId === 'personal_details') setPersonalDetailsModal(emp);
    else if (actionId === 'pension') setPensionModal(emp);
    else if (actionId === 'parental_leave') setLeaveModal(emp);
    else if (actionId === 'sick') setSickModal(emp);
    else if (actionId === 'unpaid_leave') setUnpaidLeaveModal(emp);
    else if (actionId === 'contractual_change') setContractualChangeModal(emp);
    else if (actionId === 'record_leaver') navigate(`/leaver?employee=${emp.id}`);
  };

  const derived = useMemo(() => {
    if (!employee) return null;
    const isHourly = employee.pay_type === 'hourly';
    const hourlyRate = parseFloat(employee.hourly_rate) || 0;
    const hoursThisPeriod = parseFloat(employee.hours_this_period) || 0;
    const annualVal = parseFloat(employee.annual_salary) || 0;
    // Derived from the working pattern, not the prorata_salary cache — see DashboardPage.
    const ptFrac = partTimeFractionOf(employee);
    const isPartTime = isPartTimeEmp(employee) && ptFrac < 1;
    const proRataVal = isPartTime ? annualVal * ptFrac : 0;
    const effectiveAnnual = isPartTime ? proRataVal : annualVal;
    const monthlySalary = isHourly ? (hourlyRate * hoursThisPeriod) : (effectiveAnnual / 12);
    const commission = parseFloat(employee.commission) || 0;
    const monthlyGross = monthlySalary + commission;
    const basis = employee.earnings_basis || 'qualifying';
    const isEnrolled = employee.pension_status === 'enrolled';
    const employerAmt = isEnrolled ? calculatePensionContribution(employee.employer_type, employee.employer_value, monthlyGross, basis) : 0;
    const employeeAmt = isEnrolled ? calculatePensionContribution(employee.employee_type, employee.employee_value, monthlyGross, basis) : 0;
    return { isHourly, hourlyRate, hoursThisPeriod, annualVal, proRataVal, isPartTime, effectiveAnnual, monthlySalary, commission, monthlyGross, basis, isEnrolled, employerAmt, employeeAmt };
  }, [employee]);

  // Additions & deductions for this period — from the client's pending changes
  // (this-period submissions) and the employee's standing setup items (admin-applied,
  // recurring). Kept out of the salary figure above; surfaced as their own lines/cards.
  const payItems = useMemo(() => {
    const parse = (c) => (typeof c.change_data === 'string' ? JSON.parse(c.change_data) : (c.change_data || {}));
    const todayStr = new Date().toISOString().split('T')[0];
    const stillActive = (d) => !d.cancelled_at && !(d.recurring && d.recurring_end_date && d.recurring_end_date < todayStr);
    const fromStanding = standingItems
      .filter(c => c.change_type === 'addition' || c.change_type === 'deduction')
      .map(c => ({ id: c.id, kind: c.change_type, standing: true, d: parse(c) }))
      .filter(x => stillActive(x.d));
    const fromChanges = changes
      .filter(c => c.change_type === 'addition' || c.change_type === 'deduction')
      .map(c => ({ id: c.id, kind: c.change_type, standing: false, d: parse(c) }));
    // Submitted one-offs for the current period: same period, distinct status, so
    // they never overlap with the pending or standing sets above (no double-count).
    const fromSubmitted = submittedItems
      .filter(c => c.change_type === 'addition' || c.change_type === 'deduction')
      .map(c => ({ id: c.id, kind: c.change_type, standing: false, submitted: true, d: parse(c) }));
    const all = [...fromStanding, ...fromChanges, ...fromSubmitted];
    const additions = all.filter(x => x.kind === 'addition');
    const deductions = all.filter(x => x.kind === 'deduction');
    const sum = (arr) => arr.reduce((s, x) => s + (parseFloat(x.d.amount) || 0), 0);
    const additionsTotal = sum(additions);
    const deductionsTotal = sum(deductions);
    // Pensionable additions: those not flagged non-pensionable, and not non-taxable
    // reimbursements (which never count). Mirrors the run-sheet gross_add logic.
    const pensionableAdditionsTotal = sum(additions.filter(x =>
      x.d.pensionable !== false && x.d.addition_type !== 'expense_reimbursement'));
    return { additions, deductions, additionsTotal, deductionsTotal, pensionableAdditionsTotal };
  }, [changes, standingItems, submittedItems]);

  // Pension amounts recomputed on a base that includes pensionable additions, so the
  // displayed figures match the run sheet and reflect the per-addition toggle.
  const pensionCalc = useMemo(() => {
    if (!derived || !employee) return { employerAmt: 0, employeeAmt: 0 };
    const base = derived.monthlyGross + payItems.pensionableAdditionsTotal;
    const employerAmt = derived.isEnrolled ? calculatePensionContribution(employee.employer_type, employee.employer_value, base, derived.basis) : 0;
    const employeeAmt = derived.isEnrolled ? calculatePensionContribution(employee.employee_type, employee.employee_value, base, derived.basis) : 0;
    return { employerAmt, employeeAmt };
  }, [derived, payItems, employee]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading employee…</div>;

  if (!employee) {
    return (
      <div className="max-w-[1100px] mx-auto px-8 py-7 text-center">
        <p className="text-gray-500 mb-4">Employee not found</p>
        <button onClick={() => navigate('/')} className="text-brand-700 hover:text-brand-800 font-medium">Back to dashboard</button>
      </div>
    );
  }

  const initials = `${(employee.first_name || '')[0] || ''}${(employee.last_name || '')[0] || ''}`.toUpperCase() || '·';
  const pendingSalaryChange = changes.find(c => c.change_type === 'salary' || c.change_type === 'contractual_change');
  const pendingChangeForCallout = changes[0];

  // Pension % values for ring meters (if percentage type)
  const erPct = employee.employer_type === 'percentage' ? parseFloat(employee.employer_value) || 0 : 0;
  const eePct = employee.employee_type === 'percentage' ? parseFloat(employee.employee_value) || 0 : 0;

  return (
    <div className="max-w-[1100px] mx-auto px-8 py-7">
      <button onClick={() => navigate('/')} className="text-[13px] text-gray-500 hover:text-brand-700 inline-flex items-center gap-1 mb-3">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to employees
      </button>

      {/* ── Hero card ─────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden mb-5">
        {/* Top zone */}
        <div className="px-6 py-5 grid items-center gap-5" style={{ gridTemplateColumns: '72px 1fr auto auto' }}>
          <span className="grid place-items-center w-16 h-16 rounded-full bg-brand-100 text-brand-700 text-[22px] font-bold">{initials}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-[26px] leading-tight text-ink dark:text-gray-100 truncate">{employee.first_name} {employee.last_name}</h1>
              {employee.is_director && <MiniPill tone="brand">Director</MiniPill>}
            </div>
            <p className="text-[13.5px] text-gray-500 dark:text-gray-400 mt-1 truncate">
              {[
                employee.job_title || 'No title',
                employee.start_date ? `started ${formatDate(employee.start_date)}` : null,
                employee.contract_type ? titleCase(employee.contract_type) : null,
                tenureOf(employee.start_date),
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          <StatusPill status={employee.status} />
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && !viewingAs && (
              <button
                onClick={() => viewAsClient && viewAsClient(employee.company_id)}
                className="px-3 py-2 rounded-md bg-white ring-1 ring-gray-200 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
              >
                View as client
              </button>
            )}
            <button
              data-actions-trigger
              onClick={openActionsPopover}
              aria-haspopup="menu"
              aria-expanded={!!actionsPopover}
              className="px-4 py-2 rounded-md bg-brand-600 text-white text-[13px] font-semibold hover:bg-brand-700 inline-flex items-center gap-1"
            >
              Actions <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/30">
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Annual</p>
            <p className="font-heading text-[22px] tabular leading-none mt-1.5 text-brand-700">
              {derived.isHourly ? `${formatCurrency(derived.hourlyRate)}/hr` : formatCurrency(derived.effectiveAnnual, { decimals: 'auto' })}
            </p>
            {pendingSalaryChange && (
              <p className="text-[11.5px] text-amber-700 mt-1">Change pending</p>
            )}
            {!pendingSalaryChange && derived.isPartTime && (
              <p className="text-[11.5px] text-gray-500 mt-1">FTE {formatCurrency(derived.annualVal, { decimals: 'auto' })}</p>
            )}
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">This month</p>
            <p className="font-heading text-[22px] tabular leading-none mt-1.5 text-ink">{formatCurrency(derived.monthlySalary)}</p>
            <p className="text-[11.5px] text-gray-500 mt-1">salary only</p>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Commission</p>
            <p className="font-heading text-[22px] tabular leading-none mt-1.5 text-emerald-700">{formatCurrency(derived.commission)}</p>
            <p className="text-[11.5px] text-gray-500 mt-1">this period</p>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Additions</p>
            <p className="font-heading text-[22px] tabular leading-none mt-1.5 text-emerald-700">{formatCurrency(payItems.additionsTotal)}</p>
            <p className="text-[11.5px] text-gray-500 mt-1">{payItems.deductionsTotal > 0 ? `less ${formatCurrency(payItems.deductionsTotal)} deductions` : 'this period'}</p>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Total</p>
            <p className="font-heading text-[22px] tabular leading-none mt-1.5 text-brand-700">{formatCurrency(derived.monthlySalary + derived.commission + payItems.additionsTotal)}</p>
            <p className="text-[11.5px] text-gray-500 mt-1">gross · this period</p>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Pension</p>
            <p className="font-heading text-[22px] tabular leading-none mt-1.5 text-brand-700">
              {derived.isEnrolled ? formatCurrency(pensionCalc.employerAmt + pensionCalc.employeeAmt) : '—'}
            </p>
            <p className="text-[11.5px] text-gray-500 mt-1">
              {derived.isEnrolled
                ? `${erPct ? `${erPct}% ER` : 'employer'} + ${eePct ? `${eePct}% EE` : 'employee'} · monthly`
                : titleCase(employee.pension_status || 'not enrolled')}
            </p>
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex flex-wrap gap-x-6">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`py-3 px-1 border-b-2 text-[13.5px] font-medium transition-colors inline-flex items-center gap-2 ${
                activeTab === t.id ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label}
              {t.id === 'changes' && changes.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10.5px] font-bold bg-amber-100 text-amber-800 ring-1 ring-amber-200">{changes.length}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Pay & Pension tab ────────────────────────────────────── */}
      {activeTab === 'pay' && (
        <div className="grid gap-5 lg:grid-cols-[1fr_360px] items-start">
          {/* Left: Compensation + payroll items */}
          <div className="min-w-0 space-y-5">
            <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-1 h-5 bg-brand-500 rounded-full" />
                <h3 className="font-heading text-[13px] tracking-[0.12em] uppercase text-ink dark:text-gray-100">Compensation</h3>
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                {derived.isHourly ? (
                  <>
                    <Field label="Hourly rate" value={<span className="text-[18px] font-bold text-brand-700 tabular">{formatCurrency(derived.hourlyRate)}/hr</span>} />
                    <Field label="Hours this period" value={<span className="text-[18px] font-bold text-ink tabular">{derived.hoursThisPeriod.toFixed(2)} hrs</span>} />
                    <Field label="Contracted hours/wk" value={parseFloat(employee.contracted_hours_per_week) > 0 ? `${employee.contracted_hours_per_week} hrs` : 'Zero hours'} />
                    <Field label="Pay type" value="Hourly" />
                  </>
                ) : (
                  <>
                    <Field label={derived.isPartTime ? 'Annual salary (pro-rata)' : 'Annual salary'} value={<span className="text-[18px] font-bold text-brand-700 tabular">{formatCurrency(derived.effectiveAnnual, { decimals: 'auto' })}</span>} />
                    <Field label="Monthly" value={<span className="text-[18px] font-bold text-ink tabular">{formatCurrency(derived.monthlySalary)}</span>} />
                    <Field label="Pay frequency" value={titleCase(employee.pay_frequency || 'monthly')} />
                    <Field label="Pay type" value="Salaried" />
                  </>
                )}
              </dl>

              {/* Recent change callout */}
              {pendingSalaryChange && (() => {
                const d = typeof pendingSalaryChange.change_data === 'string' ? JSON.parse(pendingSalaryChange.change_data) : pendingSalaryChange.change_data;
                const previous = d.previous_salary || employee.annual_salary;
                const next = d.new_annual_salary;
                const submittedDate = formatDate(pendingSalaryChange.created_at);
                const effective = formatDate(d.effective_date);
                return (
                  <div className="mt-4 bg-amber-50 ring-1 ring-amber-200 rounded-lg p-3.5">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700">Recent change · effective {effective}</p>
                    <p className="text-[13.5px] text-amber-900 mt-1">
                      Annual salary {next && previous ? <>changed from <span className="line-through opacity-70">{formatCurrency(previous, { decimals: 'auto' })}</span> to <strong>{formatCurrency(next, { decimals: 'auto' })}</strong></> : 'updated'}
                      {pendingSalaryChange.change_type === 'contractual_change' ? ' — via contractual change' : ''} on {submittedDate}.
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* This period's payroll items */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-1 h-5 bg-brand-500 rounded-full" />
                <h3 className="font-heading text-[13px] tracking-[0.12em] uppercase text-ink dark:text-gray-100">This period's payroll items</h3>
              </div>
              <div className="rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                <div className="grid grid-cols-[1fr_140px_120px] px-4 py-2.5 bg-gray-50 dark:bg-gray-900 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                  <span>Item</span>
                  <span className="text-right">Amount</span>
                  <span className="text-right">Status</span>
                </div>
                <div className="grid grid-cols-[1fr_140px_120px] px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 items-center">
                  <span className="text-[13.5px] text-gray-800 dark:text-gray-100">{derived.isHourly ? 'Hourly pay' : 'Base salary'} <span className="text-[12px] text-gray-500">(monthly)</span></span>
                  <span className="text-right text-[13.5px] tabular">{formatCurrency(derived.monthlySalary)}</span>
                  <span className="text-right"><MiniPill tone="violet">Standing</MiniPill></span>
                </div>
                {derived.commission > 0 && (
                  <div className="grid grid-cols-[1fr_140px_120px] px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 items-center">
                    <span className="text-[13.5px] text-gray-800 dark:text-gray-100">Commission <span className="text-[12px] text-gray-500">· this period</span></span>
                    <span className="text-right text-[13.5px] tabular text-emerald-700 font-semibold">{formatCurrency(derived.commission)}</span>
                    <span className="text-right"><MiniPill tone="emerald">Pending</MiniPill></span>
                  </div>
                )}
                {payItems.additions.map(a => (
                  <div key={a.id} className="grid grid-cols-[1fr_140px_120px] px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 items-center">
                    <span className="text-[13.5px] text-gray-800 dark:text-gray-100">
                      {a.d.addition_type_label || titleCase(a.d.addition_type) || 'Addition'}
                      <span className="text-[12px] text-gray-500"> · {a.d.recurring ? 'recurring' : 'one-off'}{a.d.pensionable === false ? ' · not pensionable' : ''}</span>
                    </span>
                    <span className="text-right text-[13.5px] tabular text-emerald-700 font-semibold">{formatCurrency(a.d.amount)}</span>
                    <span className="text-right"><MiniPill tone={a.standing ? 'violet' : a.submitted ? 'sky' : 'emerald'}>{a.standing ? 'Standing' : a.submitted ? 'Submitted' : 'Pending'}</MiniPill></span>
                  </div>
                ))}
                <div className="grid grid-cols-[1fr_140px_120px] px-4 py-3 bg-brand-50/60 border-t-2 border-brand-200 items-center">
                  <span className="text-[13px] uppercase tracking-wider font-bold text-brand-800">Gross this period</span>
                  <span className="text-right text-[14px] font-bold tabular text-brand-900">{formatCurrency(derived.monthlySalary + derived.commission + payItems.additionsTotal)}</span>
                  <span />
                </div>
                {payItems.deductions.map(dd => (
                  <div key={dd.id} className="grid grid-cols-[1fr_140px_120px] px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 items-center">
                    <span className="text-[13.5px] text-gray-800 dark:text-gray-100">
                      {dd.d.deduction_type_label || titleCase(dd.d.deduction_type) || 'Deduction'}
                      <span className="text-[12px] text-gray-500"> · {dd.d.recurring ? 'recurring' : 'one-off'}</span>
                    </span>
                    <span className="text-right text-[13.5px] tabular text-rose-600 font-semibold">&minus;{formatCurrency(dd.d.amount)}</span>
                    <span className="text-right"><MiniPill tone={dd.standing ? 'violet' : dd.submitted ? 'sky' : 'amber'}>{dd.standing ? 'Standing' : dd.submitted ? 'Submitted' : 'Pending'}</MiniPill></span>
                  </div>
                ))}
                {payItems.deductions.length > 0 && (
                  <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-[11.5px] text-gray-500">
                    Deductions are applied on the payslip (after tax where applicable) and don't reduce gross pay.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Pension + Pending callout */}
          <div className="min-w-0 space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-1 h-5 bg-brand-500 rounded-full" />
                <h3 className="font-heading text-[13px] tracking-[0.12em] uppercase text-ink dark:text-gray-100 flex-1">Pension</h3>
                {derived.isEnrolled && <MiniPill tone="emerald">Enrolled</MiniPill>}
                {employee.pension_status === 'deferred' && <MiniPill tone="sky">Deferred</MiniPill>}
                {employee.pension_status === 'opted-out' && <MiniPill tone="amber">Opted out</MiniPill>}
                {employee.pension_status === 'not-eligible' && <MiniPill tone="gray">Not eligible</MiniPill>}
                {employee.pension_status === 'exempt' && <MiniPill tone="violet">Exempt</MiniPill>}
              </div>

              {derived.isEnrolled ? (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <RingMeter percent={erPct * 10} color="#ab63f2" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-ink">Employer</p>
                        <p className="text-[11.5px] text-gray-500">
                          {employee.employer_type === 'percentage' ? `${employee.employer_value}% contribution` : `${formatCurrency(employee.employer_value)} fixed`}
                        </p>
                      </div>
                      <span className="text-[14px] font-semibold tabular text-gray-800">{formatCurrency(pensionCalc.employerAmt)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <RingMeter percent={eePct * 10} color="#7b32b5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-ink">Employee</p>
                        <p className="text-[11.5px] text-gray-500">
                          {employee.employee_type === 'percentage' ? `${employee.employee_value}% contribution` : `${formatCurrency(employee.employee_value)} fixed`}
                        </p>
                      </div>
                      <span className="text-[14px] font-semibold tabular text-gray-800">{formatCurrency(pensionCalc.employeeAmt)}</span>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 mt-4 pt-3 flex items-baseline justify-between">
                    <span className="text-[12px] uppercase tracking-wider font-semibold text-gray-500">Total / month</span>
                    <span className="text-[18px] font-bold tabular text-brand-700">{formatCurrency(pensionCalc.employerAmt + pensionCalc.employeeAmt)}</span>
                  </div>
                  <p className="text-[11.5px] text-gray-500 italic mt-2">
                    {titleCase(derived.basis)} earnings basis{payItems.pensionableAdditionsTotal > 0 ? ` · incl. ${formatCurrency(payItems.pensionableAdditionsTotal)} additions` : ''}
                  </p>
                </>
              ) : (
                <p className="text-[13px] text-gray-500">
                  {employee.pension_status === 'deferred' ? 'Pension assessment deferred — no contributions this period.'
                    : employee.pension_status === 'opted-out' ? 'Employee has opted out of the workplace pension.'
                      : employee.pension_status === 'not-eligible' ? 'Not currently eligible for the workplace pension.'
                        : employee.pension_status === 'exempt' ? 'Exempt from auto-enrolment — outside the workplace pension rules.'
                          : 'Pension status not yet set.'}
                </p>
              )}
            </div>

            {pendingChangeForCallout && (
              <div className="bg-amber-50 ring-1 ring-amber-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700">Pending change</p>
                  <MiniPill tone="amber">In this period</MiniPill>
                </div>
                <p className="text-[13.5px] text-amber-900 mt-1.5">
                  <strong>{CHANGE_TYPE_LABELS[pendingChangeForCallout.change_type] || pendingChangeForCallout.change_type}</strong> · {changeSummary(pendingChangeForCallout)}
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={() => setActiveTab('changes')}
                    className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-amber-800 ring-1 ring-amber-300 bg-white hover:bg-amber-100">Edit</button>
                  <button onClick={async () => {
                    try { await api.delete(`/pending-changes/${pendingChangeForCallout.id}`); setChanges(prev => prev.filter(c => c.id !== pendingChangeForCallout.id)); }
                    catch (err) { console.error('Failed to undo:', err); }
                  }} className="text-[12.5px] text-rose-600 hover:text-rose-700 font-medium">Undo</button>
                </div>
                {changes.length > 1 && (
                  <p className="text-[11.5px] text-amber-700 mt-2">+{changes.length - 1} more in the Pending changes tab</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Employment tab ───────────────────────────────────────── */}
      {activeTab === 'employment' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
            <Field label="Start date" value={formatDate(employee.start_date)} />
            {employee.leaving_date && <Field label="Leaving date" value={formatDate(employee.leaving_date)} />}
            <Field label="Job title" value={employee.job_title} />
            <Field label="Contract type" value={titleCase(employee.contract_type)} />
            {employee.contract_type === 'fixed-term' && <Field label="Contract end" value={formatDate(employee.contract_end_date)} />}
            {isPartTimeEmp(employee) && <Field label="Part-time basis" value={employee.part_time_basis} />}
            <Field label="Pay frequency" value={titleCase(employee.pay_frequency)} />
            <Field label="Student loan" value={employee.student_loan === 'none' ? 'None' : employee.student_loan?.toUpperCase()} />
            <Field label="Director" value={employee.is_director ? 'Yes' : 'No'} />
            {employee.is_director && <Field label="Director NI method" value={DIRECTOR_NI_LABELS[employee.director_ni_method] || 'Not recorded'} />}
            {employee.is_director && employee.director_appointed_date && <Field label="Appointed director" value={formatDate(employee.director_appointed_date)} />}
            <Field label="Tax code" value={employee.tax_code} />
          </dl>
        </div>
      )}

      {/* ── Personal tab ─────────────────────────────────────────── */}
      {activeTab === 'personal' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-1 h-5 bg-brand-500 rounded-full" />
              <h3 className="font-heading text-[13px] tracking-[0.12em] uppercase text-ink dark:text-gray-100">Personal information</h3>
              <button
                type="button"
                onClick={() => setPersonalDetailsModal(employee)}
                className="ml-auto text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
              >
                Edit
              </button>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              <Field label="First name" value={employee.first_name} />
              <Field label="Last name" value={employee.last_name} />
              <Field label="Email" value={employee.email} />
              <Field label="Gender" value={titleCase(employee.gender)} />
              <Field label="Date of birth" value={formatDate(employee.date_of_birth)} />
              <Field label="NI number" value={employee.ni_number ? `****${employee.ni_number.slice(-3)}` : null} />
            </dl>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-1 h-5 bg-brand-500 rounded-full" />
              <h3 className="font-heading text-[13px] tracking-[0.12em] uppercase text-ink dark:text-gray-100">Address</h3>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              <Field label="Address line 1" value={employee.address} />
              <Field label="Address line 2" value={employee.address_line_2} />
              <Field label="City" value={employee.city} />
              <Field label="Postcode" value={employee.postcode} />
            </dl>
          </div>
        </div>
      )}

      {/* ── Bank tab ─────────────────────────────────────────────── */}
      {activeTab === 'bank' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
            <Field label="Account name" value={employee.bank_account_name} />
            <Field label="Sort code" value={employee.sort_code ? `**-**-${employee.sort_code.slice(-2)}` : null} />
            <Field label="Account number" value={employee.account_number ? `****${employee.account_number.slice(-4)}` : null} />
            {employee.roll_number && <Field label="Roll number" value={employee.roll_number} />}
          </dl>
        </div>
      )}

      {/* ── Pending changes tab ──────────────────────────────────── */}
      {activeTab === 'changes' && (
        <div>
          {changes.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-8 text-center text-gray-500">
              No pending changes for this employee.
            </div>
          ) : (
            <div className="space-y-3">
              {changes.map(change => (
                <div key={change.id} className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <MiniPill tone="brand">{CHANGE_TYPE_LABELS[change.change_type] || change.change_type}</MiniPill>
                      <span className="text-[13.5px] text-gray-700 dark:text-gray-200 truncate">{changeSummary(change)}</span>
                    </div>
                    <span className="text-[11.5px] text-gray-400 tabular shrink-0">{new Date(change.created_at).toLocaleString('en-GB')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── History tab ──────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-8 text-center text-gray-500">
          <p className="text-[13.5px]">Employee change history is on the roadmap.</p>
          <p className="text-[12px] text-gray-400 mt-1">For now the Submission History page shows everything that's been sent through.</p>
        </div>
      )}

      {/* Actions popover + modals — wired identically to the dashboard */}
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      {actionsPopover && (
        <EmployeeActionsPopover
          emp={actionsPopover.emp}
          anchorRect={actionsPopover.rect}
          sickRecords={sickRecords}
          onClose={() => setActionsPopover(null)}
          onSelectAction={handleActionSelect}
        />
      )}

      {pensionModal && (
        <PensionModal employee={pensionModal} pension={pensionModal} onSave={handlePensionSave} onClose={() => setPensionModal(null)} pensionableAdditions={payItems.pensionableAdditionsTotal} />
      )}
      {leaveModal && (
        <LeaveModal employee={leaveModal} onSave={handleLeaveSave} onClose={() => setLeaveModal(null)} />
      )}
      {sickModal && (
        <SickModal
          employee={sickModal}
          existingSickRecord={sickRecords.find(r => r.employee_id === sickModal.id)}
          onSave={handleSickSave}
          onClose={() => setSickModal(null)}
        />
      )}
      {unpaidLeaveModal && (
        <UnpaidLeaveModal employee={unpaidLeaveModal} onSave={handleUnpaidLeaveSave} onClose={() => setUnpaidLeaveModal(null)} />
      )}
      {additionModal && (
        <AdditionModal
          employee={additionModal}
          onSave={handleAdditionSave}
          onClose={() => setAdditionModal(null)}
          activeParentalLeaves={changes
            .filter(c => c.change_type === 'parental_leave')
            .map(c => ({
              id: c.id,
              leave_type: c.change_data?.leave_type,
              start_date: c.change_data?.desired_leave_start_date || c.change_data?.start_date,
              expected_return_date: c.change_data?.expected_return_date,
            }))}
        />
      )}
      {deductionModal && (
        <DeductionModal employee={deductionModal} onSave={handleDeductionSave} onClose={() => setDeductionModal(null)} />
      )}
      {bankDetailsModal && (
        <BankDetailsModal employee={bankDetailsModal} onSave={handleBankDetailsSave} onClose={() => setBankDetailsModal(null)} />
      )}
      {addressModal && (
        <AddressModal employee={addressModal} onSave={handleAddressSave} onClose={() => setAddressModal(null)} />
      )}
      {personalDetailsModal && (
        <PersonalDetailsModal employee={personalDetailsModal} onSave={handlePersonalDetailsSave} onClose={() => setPersonalDetailsModal(null)} />
      )}
      {contractualChangeModal && (
        <ContractualChangeModal employee={contractualChangeModal} onSave={handleContractualChangeSave} onClose={() => setContractualChangeModal(null)} />
      )}
    </div>
  );
}
